import type { AlertSettings, DashboardHandoff } from "./contracts";
import type {
  BrowserObservationCommand,
  BrowserObservationResult,
} from "./browser-observation";

export const HOSTED_APP_SOURCE = "411-agent-desk-app";
export const HOSTED_EXTENSION_SOURCE = "411-agent-desk-host";
export const HOSTED_PROTOCOL_VERSION = 1;
export const TOOLBAR_SHELL_SOURCE = "411-agent-desk-shell";
export const TOOLBAR_CONTENT_SOURCE = "411-agent-desk-content";
// Sections are opaque identifiers inside the fixed hosted app, never URLs.
export type AgentToolSection = string;
export function isAgentToolSection(value: unknown): value is AgentToolSection {
  return typeof value === "string" && /^[a-z][a-z0-9_-]{0,63}$/.test(value);
}
export interface ToolbarShellEvent {
  source: typeof TOOLBAR_SHELL_SOURCE;
  version: 1;
  type: "toolbar-ready";
  ready: boolean;
}
export type ToolbarFailureCode =
  | "T-CONFIG"
  | "T-HOST-AUTH"
  | "T-HOST-FRAME"
  | "T-HOST-LOAD"
  | "T-HOST-TIMEOUT";

export interface ToolbarShellErrorEvent {
  source: typeof TOOLBAR_SHELL_SOURCE;
  version: typeof HOSTED_PROTOCOL_VERSION;
  type: "toolbar-error";
  code: ToolbarFailureCode;
}

export function isToolbarFailureCode(
  value: unknown,
): value is ToolbarFailureCode {
  return (
    value === "T-CONFIG" ||
    value === "T-HOST-AUTH" ||
    value === "T-HOST-FRAME" ||
    value === "T-HOST-LOAD" ||
    value === "T-HOST-TIMEOUT"
  );
}
export interface ToolbarContentEvent {
  source: typeof TOOLBAR_CONTENT_SOURCE;
  version: 1;
  type: "focus-tools";
}
export interface ContentSectionEvent {
  source: typeof TOOLBAR_CONTENT_SOURCE;
  version: 1;
  type: "open-section";
  section: AgentToolSection;
}
export interface ContentSurfaceStateRequest {
  source: typeof TOOLBAR_CONTENT_SOURCE;
  version: 1;
  type: "request-surface-state";
}
export interface DrawerReadyEvent {
  source: typeof HOSTED_APP_SOURCE;
  version: 1;
  type: "drawer-ready";
}
export interface DrawerShellEvent {
  source: typeof TOOLBAR_SHELL_SOURCE;
  version: 1;
  type: "drawer-ready";
  ready: boolean;
}
export interface DrawerShellErrorEvent {
  source: typeof TOOLBAR_SHELL_SOURCE;
  version: 1;
  type: "drawer-error";
}
export interface HostedSectionEvent {
  source: typeof HOSTED_EXTENSION_SOURCE;
  version: 1;
  type: "open-section";
  section: AgentToolSection;
}

export interface HostedFocusEvent {
  source: typeof HOSTED_EXTENSION_SOURCE;
  version: 1;
  type: "focus-tools";
}

export interface HostedDrawerVisibilityEvent {
  source: typeof HOSTED_EXTENSION_SOURCE;
  version: typeof HOSTED_PROTOCOL_VERSION;
  type: "drawer-visibility";
  visible: boolean;
}

export type HostedRequest = {
  source: typeof HOSTED_APP_SOURCE;
  version: typeof HOSTED_PROTOCOL_VERSION;
  requestId: string;
} & (
  | { type: "connect"; challenge: string }
  | { type: "close" }
  | { type: "alert-settings"; settings?: AlertSettings }
  | { type: "open-tools"; section: AgentToolSection }
  | { type: "dashboard" }
  | { type: "surface-ready"; ready: boolean }
  | { type: "browser-observation"; command: BrowserObservationCommand }
);

export interface HostedResponse {
  source: typeof HOSTED_EXTENSION_SOURCE;
  version: typeof HOSTED_PROTOCOL_VERSION;
  requestId: string;
  type: HostedRequest["type"];
  ok: boolean;
  data?: DashboardHandoff | AlertSettings | BrowserObservationResult;
  error?: string;
  status?: number;
}

export interface HostedSessionResetEvent {
  source: typeof HOSTED_EXTENSION_SOURCE;
  version: typeof HOSTED_PROTOCOL_VERSION;
  type: "session-reset";
}
